import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  MapPin, RefreshCw, Users, Clock, Navigation, Phone, Mail,
  Signal, SignalLow, SignalZero, ChevronRight, Locate, ExternalLink, Link2, X
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import salesApiClient from '@/api/salesClient';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons for different roles
const createCustomIcon = (color, initials, hasLocationLink, extractedCoords) => {
  const borderColor = extractedCoords ? '#22c55e' : (hasLocationLink ? '#3b82f6' : 'white');
  const badge = extractedCoords 
    ? '<span style="position:absolute;top:-2px;right:-2px;width:12px;height:12px;background:#22c55e;border-radius:50%;border:2px solid white;"></span>'
    : (hasLocationLink 
        ? '<span style="position:absolute;top:-2px;right:-2px;width:12px;height:12px;background:#3b82f6;border-radius:50%;border:2px solid white;"></span>'
        : '');
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background: ${color};
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 14px;
        border: 3px solid ${borderColor};
        box-shadow: 0 2px 10px rgba(0,0,0,0.3);
        position: relative;
      ">${initials}${badge}</div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });
};

// Role colors
const roleColors = {
  ceo: '#ef4444',      // red
  cso: '#f97316',      // orange
  director: '#eab308', // yellow
  manager: '#22c55e',  // green
  sales_rep: '#3b82f6', // blue
  admin: '#a855f7',    // purple
};

// Component to fit map bounds
function FitBounds({ locations }) {
  const map = useMap();
  
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(loc => [loc.latitude, loc.longitude]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);
  
  return null;
}

export default function TeamLocatorPage() {
  const [teamLocations, setTeamLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [viewingMember, setViewingMember] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [nextRefresh, setNextRefresh] = useState(30);
  const mapRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || localStorage.getItem('salesUser') || localStorage.getItem('userData') || '{}');
  
  // Check if user has C-level access (check both role and sales_role)
  const cLevelRoles = ['ceo', 'cso', 'director', 'super_admin', 'admin', 'manager'];
  const hasAccess = cLevelRoles.includes(user.role) || cLevelRoles.includes(user.sales_role);

  // GPS refresh interval - 30 seconds
  const REFRESH_INTERVAL = 30;

  useEffect(() => {
    if (hasAccess) {
      fetchTeamLocations();
      
      // Auto-refresh GPS every 30 seconds
      const refreshInterval = setInterval(() => {
        fetchTeamLocations();
        setNextRefresh(REFRESH_INTERVAL);
      }, REFRESH_INTERVAL * 1000);
      
      // Countdown timer
      const countdownInterval = setInterval(() => {
        setNextRefresh(prev => prev > 0 ? prev - 1 : REFRESH_INTERVAL);
      }, 1000);
      
      return () => {
        clearInterval(refreshInterval);
        clearInterval(countdownInterval);
      };
    }
  }, [hasAccess]);

  const fetchTeamLocations = async () => {
    setIsRefreshing(true);
    try {
      // Fetch team members with their google_location_link
      const membersResponse = await salesApiClient.get('/team/members');
      const members = membersResponse.data || [];
      
      // Malaysian demo locations for fallback
      const demoLocations = [
        { lat: 3.1390, lng: 101.6869, loc: 'Kuala Lumpur HQ' },
        { lat: 3.1569, lng: 101.7123, loc: 'Client Meeting - KLCC' },
        { lat: 3.1209, lng: 101.6538, loc: 'Bangsar Office' },
        { lat: 3.0738, lng: 101.5183, loc: 'Shah Alam - Client Visit' },
        { lat: 3.1478, lng: 101.6953, loc: 'Bukit Bintang - Prospect Meeting' },
        { lat: 3.1319, lng: 101.6841, loc: 'En route to Petaling Jaya' },
        { lat: 3.1067, lng: 101.6655, loc: 'Mid Valley - Demo Session' },
        { lat: 3.1750, lng: 101.6993, loc: 'Ampang - Follow-up' },
        { lat: 3.0319, lng: 101.3685, loc: 'Klang - Site Visit' },
      ];
      
      const statuses = ['online', 'online', 'online', 'online', 'online', 'away', 'online', 'online', 'offline'];
      const lastSeenOptions = ['Just now', '2 mins ago', '5 mins ago', '3 mins ago', '1 min ago', '15 mins ago', '4 mins ago', '8 mins ago', '2 hours ago'];
      
      // Map members to location data with real GPS extraction
      const locationsData = await Promise.all(members.map(async (member, idx) => {
        const demoLoc = demoLocations[idx % demoLocations.length];
        const hasLiveLocation = !!member.google_location_link;
        
        let latitude = demoLoc.lat;
        let longitude = demoLoc.lng;
        let locationName = hasLiveLocation ? 'Live Location Enabled' : demoLoc.loc;
        let extractedCoords = false;
        
        // Try to extract real GPS coordinates from Google Maps link
        if (hasLiveLocation) {
          try {
            const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
            const extractResponse = await fetch(`${apiUrl}/api/location/extract`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ url: member.google_location_link })
            });
            
            if (extractResponse.ok) {
              const coords = await extractResponse.json();
              if (coords.latitude && coords.longitude) {
                latitude = coords.latitude;
                longitude = coords.longitude;
                locationName = `Live: ${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`;
                extractedCoords = true;
              }
            }
          } catch (err) {
            console.log(`Could not extract coords for ${member.name}:`, err);
          }
        }
        
        return {
          id: member.id,
          name: member.name,
          role: member.role,
          email: member.email,
          latitude,
          longitude,
          location: locationName,
          status: hasLiveLocation ? 'online' : statuses[idx % statuses.length],
          lastSeen: extractedCoords ? 'Real-time GPS' : (hasLiveLocation ? 'Live' : lastSeenOptions[idx % lastSeenOptions.length]),
          google_location_link: member.google_location_link || null,
          hasLiveLocation,
          extractedCoords
        };
      }));
      
      setTeamLocations(locationsData);
      setLastUpdated(new Date());
      setNextRefresh(REFRESH_INTERVAL);
    } catch (error) {
      console.log('Error fetching team data, using fallback');
      // Fallback mock data
      const mockLocations = [
        { id: '1', name: 'Anu', role: 'ceo', email: 'anu@aurous.com', latitude: 3.1390, longitude: 101.6869, status: 'online', lastSeen: 'Just now', location: 'Kuala Lumpur HQ', hasLiveLocation: false },
        { id: '2', name: 'Ranjan', role: 'cso', email: 'ranjan@aurous.com', latitude: 3.1569, longitude: 101.7123, status: 'online', lastSeen: '2 mins ago', location: 'Client Meeting - KLCC', hasLiveLocation: false },
        { id: '3', name: 'Dheeraj', role: 'director', email: 'dheeraj@aurous.com', latitude: 3.1209, longitude: 101.6538, status: 'online', lastSeen: '5 mins ago', location: 'Bangsar Office', hasLiveLocation: false },
      ];
      setTeamLocations(mockLocations);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Manual refresh handler
  const handleManualRefresh = () => {
    fetchTeamLocations();
    setNextRefresh(REFRESH_INTERVAL);
  };

  const openLiveLocation = (member) => {
    setViewingMember(member);
    setLocationDialogOpen(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online': return <Signal className="w-4 h-4 text-green-400" />;
      case 'away': return <SignalLow className="w-4 h-4 text-yellow-400" />;
      default: return <SignalZero className="w-4 h-4 text-slate-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'online': return <Badge className="bg-green-500/20 text-green-400 border-0 text-xs">Online</Badge>;
      case 'away': return <Badge className="bg-yellow-500/20 text-yellow-400 border-0 text-xs">Away</Badge>;
      default: return <Badge className="bg-slate-500/20 text-slate-400 border-0 text-xs">Offline</Badge>;
    }
  };

  const getRoleBadge = (role) => {
    const labels = {
      ceo: 'CEO',
      cso: 'CSO',
      director: 'Director',
      manager: 'Sales Director',
      sales_rep: 'Sales Executive',
      admin: 'Admin'
    };
    return labels[role] || role;
  };

  const focusOnMember = (member) => {
    setSelectedMember(member);
    if (mapRef.current) {
      mapRef.current.flyTo([member.latitude, member.longitude], 15, { duration: 1 });
    }
  };

  if (!hasAccess) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Card className="bg-slate-800/50 border-slate-700/50 max-w-md">
          <CardContent className="p-8 text-center">
            <MapPin className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Access Restricted</h2>
            <p className="text-slate-400">
              Team location tracking is only available for C-Level executives and Directors.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Locate className="w-7 h-7 text-blue-400" />
            Locate Your Team
          </h1>
          <p className="text-slate-400 mt-1">
            Real-time location tracking of your sales team
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Auto-refresh indicator */}
          <div className="flex items-center gap-2 text-sm">
            <div className={`w-2 h-2 rounded-full ${isRefreshing ? 'bg-green-500 animate-pulse' : 'bg-slate-500'}`} />
            <span className="text-slate-400">
              {isRefreshing ? 'Updating GPS...' : `Next refresh: ${nextRefresh}s`}
            </span>
          </div>
          
          {lastUpdated && (
            <span className="text-sm text-slate-500">
              <Clock className="w-4 h-4 inline mr-1" />
              {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          
          <Button 
            onClick={handleManualRefresh} 
            variant="outline" 
            size="sm"
            disabled={isRefreshing}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Link2 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-green-400">
                {teamLocations.filter(m => m.extractedCoords).length}
              </div>
              <div className="text-xs text-slate-400">Real GPS</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Link2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">
                {teamLocations.filter(m => m.hasLiveLocation && !m.extractedCoords).length}
              </div>
              <div className="text-xs text-slate-400">Link Only</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Signal className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">
                {teamLocations.filter(m => m.status === 'online').length}
              </div>
              <div className="text-xs text-slate-400">Online</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <SignalLow className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">
                {teamLocations.filter(m => m.status === 'away').length}
              </div>
              <div className="text-xs text-slate-400">Away</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-500/20 flex items-center justify-center">
              <SignalZero className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">
                {teamLocations.filter(m => m.status === 'offline').length}
              </div>
              <div className="text-xs text-slate-400">Offline</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{teamLocations.length}</div>
              <div className="text-xs text-slate-400">Total Team</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - Map and List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <Card className="lg:col-span-2 bg-slate-800/50 border-slate-700/50 overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-400" />
              Live Map
              <span className="ml-auto text-xs font-normal text-slate-400">
                Green border = Live tracking enabled
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="h-[500px] relative">
              {teamLocations.length > 0 && (
                <MapContainer
                  ref={mapRef}
                  center={[3.1390, 101.6869]} // Kuala Lumpur
                  zoom={11}
                  style={{ height: '100%', width: '100%' }}
                  className="rounded-b-xl"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <FitBounds locations={teamLocations} />
                  
                  {teamLocations.map((member) => (
                    <Marker
                      key={member.id}
                      position={[member.latitude, member.longitude]}
                      icon={createCustomIcon(roleColors[member.role] || '#3b82f6', member.name.substring(0, 2).toUpperCase(), member.hasLiveLocation, member.extractedCoords)}
                    >
                      <Popup>
                        <div className="p-2 min-w-[220px]">
                          <div className="flex items-center gap-2 mb-2">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold relative"
                              style={{ background: roleColors[member.role] }}
                            >
                              {member.name.substring(0, 2).toUpperCase()}
                              {member.extractedCoords && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                              )}
                              {member.hasLiveLocation && !member.extractedCoords && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full border-2 border-white" />
                              )}
                            </div>
                            <div>
                              <div className="font-bold text-slate-900">{member.name}</div>
                              <div className="text-xs text-slate-500">{getRoleBadge(member.role)}</div>
                            </div>
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-slate-400" />
                              <span>{member.location}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-slate-400" />
                              <span>{member.lastSeen}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-slate-400" />
                              <span className="text-xs">{member.email}</span>
                            </div>
                          </div>
                          {member.hasLiveLocation && (
                            <button
                              onClick={() => openLiveLocation(member)}
                              className="mt-3 w-full px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg flex items-center justify-center gap-2"
                            >
                              <ExternalLink className="w-4 h-4" />
                              View Live Location
                            </button>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Team List */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" />
              Team Members
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="space-y-2 max-h-[460px] overflow-y-auto">
              {teamLocations.map((member) => (
                <div
                  key={member.id}
                  className={`p-3 rounded-lg transition-all ${
                    selectedMember?.id === member.id 
                      ? 'bg-blue-500/20 border border-blue-500/50' 
                      : member.hasLiveLocation
                        ? 'bg-green-500/10 border border-green-500/30 hover:bg-green-500/20'
                        : 'bg-slate-700/30 hover:bg-slate-700/50 border border-transparent'
                  }`}
                >
                  <div 
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => focusOnMember(member)}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm relative"
                      style={{ background: roleColors[member.role] }}
                    >
                      {member.name.substring(0, 2).toUpperCase()}
                      {member.hasLiveLocation && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-800" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white truncate">{member.name}</span>
                        {getStatusIcon(member.status)}
                      </div>
                      <div className="text-xs text-slate-400 truncate">{member.location}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <Badge 
                      className="text-xs border-0"
                      style={{ 
                        background: `${roleColors[member.role]}20`,
                        color: roleColors[member.role]
                      }}
                    >
                      {getRoleBadge(member.role)}
                    </Badge>
                    {member.extractedCoords ? (
                      <button
                        onClick={() => openLiveLocation(member)}
                        className="text-xs px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded flex items-center gap-1"
                      >
                        <Navigation className="w-3 h-3" />
                        GPS
                      </button>
                    ) : member.hasLiveLocation ? (
                      <button
                        onClick={() => openLiveLocation(member)}
                        className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View
                      </button>
                    ) : (
                      <span className="text-xs text-slate-500">{member.lastSeen}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Legend */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-6">
            <span className="text-sm text-slate-400">Role Colors:</span>
            {Object.entries(roleColors).map(([role, color]) => (
              <div key={role} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ background: color }}
                />
                <span className="text-sm text-slate-300 capitalize">
                  {role === 'sales_rep' ? 'Sales Executive' : role === 'cso' ? 'CSO' : role}
                </span>
              </div>
            ))}
            <div className="flex items-center gap-4 ml-4 pl-4 border-l border-slate-600">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 border-green-500 bg-slate-700" />
                <span className="text-sm text-green-400">Real GPS</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full border-2 border-blue-500 bg-slate-700" />
                <span className="text-sm text-blue-400">Link Only</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Live Location Dialog */}
      <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-4xl w-[95vw] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-400" />
              Live Location - {viewingMember?.name}
              <Badge className="ml-2 bg-green-500/20 text-green-400 border-0">
                Real-time
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {viewingMember && viewingMember.google_location_link && (
            <div className="space-y-4">
              {/* Embedded Google Maps iframe */}
              <div className="relative w-full h-[500px] bg-slate-800 rounded-lg overflow-hidden">
                <iframe
                  src={viewingMember.google_location_link}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Live location of ${viewingMember.name}`}
                  className="rounded-lg"
                />
              </div>
              
              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                  onClick={() => window.open(viewingMember.google_location_link, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open in Google Maps
                </Button>
                <Button
                  variant="default"
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    navigator.clipboard.writeText(viewingMember.google_location_link);
                    // Could add toast here
                  }}
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy Location Link
                </Button>
              </div>
              
              {/* Info box */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-300 mb-2 flex items-center gap-2">
                  <Signal className="w-4 h-4" />
                  Live Location Info
                </h4>
                <p className="text-xs text-slate-400">
                  This is a live Google Maps location sharing link. The location updates in real-time 
                  as {viewingMember.name} moves. The sharing will remain active until they disable it 
                  from their Google Maps app.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
